import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  WorkOutline as StaffIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../contexts/ApiContext';
import CreateRoleDialog from './CreateRoleDialog';
import CreatePermissionGroupDialog from './CreatePermissionGroupDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`permissions-tabpanel-${index}`}
      aria-labelledby={`permissions-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface Role {
  id: string;
  name: string;
  description: string;
  isDefault: boolean;
  userCount: number;
  permissions: string[];
}

interface PermissionGroup {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  assignedRoles: string[];
}

interface Permission {
  id: string;
  name: string;
  resource: string;
  action: string;
  description: string;
}

const Permissions: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const api = useApi();
  const queryClient = useQueryClient();

  // Fetch roles from API
  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get('/roles'),
  });

  // Fetch permission groups from API
  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: ['permission-groups'],
    queryFn: () => api.get('/permission-groups'),
  });

  // Fetch all permissions from API
  const { data: permissionsData, isLoading: permissionsLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => api.get('/permissions'),
  });

  const roles = rolesData?.roles || [];
  const permissionGroups = groupsData?.permissionGroups || [];
  const availablePermissions = permissionsData?.permissions || [];

  // Create role mutation
  const createRoleMutation = useMutation({
    mutationFn: (roleData: any) => api.post('/roles', roleData),
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      setSnackbar({ open: true, message: 'Role created successfully', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to create role', severity: 'error' });
    },
  });

  // Create permission group mutation
  const createGroupMutation = useMutation({
    mutationFn: (groupData: any) => api.post('/permission-groups', groupData),
    onSuccess: () => {
      queryClient.invalidateQueries(['permission-groups']);
      setSnackbar({ open: true, message: 'Permission group created successfully', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to create permission group', severity: 'error' });
    },
  });

  // Delete role mutation
  const deleteRoleMutation = useMutation({
    mutationFn: (roleId: string) => api.delete(`/roles/${roleId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['roles']);
      setSnackbar({ open: true, message: 'Role deleted successfully', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to delete role', severity: 'error' });
    },
  });

  // Delete permission group mutation
  const deleteGroupMutation = useMutation({
    mutationFn: (groupId: string) => api.delete(`/permission-groups/${groupId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['permission-groups']);
      setSnackbar({ open: true, message: 'Permission group deleted successfully', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to delete permission group', severity: 'error' });
    },
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleCreateRole = (roleData: any) => {
    createRoleMutation.mutate(roleData);
  };

  const handleCreateGroup = (groupData: any) => {
    createGroupMutation.mutate(groupData);
  };

  const handleDeleteRole = (roleId: string) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      deleteRoleMutation.mutate(roleId);
    }
  };

  const handleDeleteGroup = (groupId: string) => {
    if (window.confirm('Are you sure you want to delete this permission group?')) {
      deleteGroupMutation.mutate(groupId);
    }
  };

  const getRoleIcon = (roleName: string) => {
    switch (roleName.toLowerCase()) {
      case 'admin':
        return <AdminIcon color="error" />;
      case 'staff':
        return <StaffIcon color="primary" />;
      case 'user':
        return <PersonIcon color="action" />;
      default:
        return <GroupIcon />;
    }
  };

  const getRoleColor = (roleName: string) => {
    switch (roleName.toLowerCase()) {
      case 'admin':
        return 'error';
      case 'staff':
        return 'primary';
      case 'user':
        return 'default';
      default:
        return 'secondary';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Permissions Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage roles, permission groups, and access control
          </Typography>
        </Box>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Permission System Overview
        </Typography>
        <Typography variant="body2">
          • <strong>Admin</strong> role has full access to all features
          <br />
          • <strong>Staff</strong> role requires specific permissions per endpoint
          <br />
          • <strong>User</strong> role has limited access to their own resources
          <br />
          • Permission groups can be assigned to roles for easier management
        </Typography>
      </Alert>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="permissions tabs">
          <Tab label="Roles" icon={<GroupIcon />} iconPosition="start" />
          <Tab label="Permission Groups" icon={<SecurityIcon />} iconPosition="start" />
          <Tab label="All Permissions" icon={<CheckIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {/* Roles Tab */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Roles ({roles.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setRoleDialogOpen(true)}
          >
            Create Role
          </Button>
        </Box>

        {rolesLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
          {roles.map((role: any) => (
            <Grid item xs={12} md={6} lg={4} key={role.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getRoleIcon(role.name)}
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {role.name}
                      </Typography>
                    </Box>
                    {!role.isDefault && (
                      <Box>
                        <IconButton size="small" color="primary">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteRole(role.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {role.description}
                  </Typography>

                  <Box display="flex" gap={1} sx={{ mb: 2 }}>
                    <Chip
                      label={role.isDefault ? 'Default Role' : 'Custom Role'}
                      size="small"
                      color={role.isDefault ? 'primary' : 'default'}
                    />
                    <Chip
                      label={`${role.userCount} users`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                    PERMISSIONS ({role.permissions.length})
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {role.permissions.slice(0, 3).map((perm: any, idx: number) => (
                      <Chip key={idx} label={perm} size="small" variant="outlined" />
                    ))}
                    {role.permissions.length > 3 && (
                      <Chip label={`+${role.permissions.length - 3} more`} size="small" />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {/* Permission Groups Tab */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Permission Groups ({permissionGroups.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setGroupDialogOpen(true)}
          >
            Create Permission Group
          </Button>
        </Box>

        {groupsLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
          {permissionGroups.map((group: any) => (
            <Grid item xs={12} md={6} key={group.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <SecurityIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {group.name}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton size="small" color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteGroup(group.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {group.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                    ASSIGNED ROLES
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
                    {group.assignedRoles.map((role: any, idx: number) => (
                      <Chip
                        key={idx}
                        label={role}
                        size="small"
                        color={getRoleColor(role) as any}
                      />
                    ))}
                  </Box>

                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                    PERMISSIONS ({group.permissions.length})
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {group.permissions.slice(0, 4).map((perm: any, idx: number) => (
                      <Chip key={idx} label={perm} size="small" variant="outlined" />
                    ))}
                    {group.permissions.length > 4 && (
                      <Chip label={`+${group.permissions.length - 4} more`} size="small" />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {/* All Permissions Tab */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            All Available Permissions ({availablePermissions.length})
          </Typography>
        </Box>

        {permissionsLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Permission Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Resource</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Permission Key</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availablePermissions.map((permission: any) => (
                <TableRow key={permission.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {permission.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={permission.resource} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={permission.action} size="small" color="secondary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {permission.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {permission.resource}:{permission.action}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Create Role Dialog */}
      <CreateRoleDialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
        onSave={handleCreateRole}
        availablePermissions={availablePermissions}
        permissionGroups={permissionGroups}
      />

      {/* Create Permission Group Dialog */}
      <CreatePermissionGroupDialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        onSave={handleCreateGroup}
        availablePermissions={availablePermissions}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Permissions;

